package org.moxultimate.topwithmox.appscrud.main

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.apps.data.AppsResponse
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.cards.ContentCard
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.theme.*
import org.w3c.dom.events.Event

/**
 * AppsCRUDPage
 * Main CRUD landing page for /apps-crud. Lists all apps with search/filter controls,
 * an Add button, and click-to-edit navigation on each card.
 */
@Composable
fun AppsCRUDPage() {
    val viewModel: AppsCRUDViewModel = koinViewModel()
    val listState by viewModel.listState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val allTypes by viewModel.allTypes.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val searchText by viewModel.searchText.collectAsState()
    val hasMoreInCurrentView by viewModel.hasMoreInCurrentView.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val crudState by viewModel.crudState.collectAsState()
    val navigator = rememberAppNavigator()

    var lastSuccess by remember { mutableStateOf<AppsListState.Success?>(null) }
    if (listState is AppsListState.Success) {
        lastSuccess = listState as AppsListState.Success
    }

    val isFilterReloading = listState is AppsListState.Loading && lastSuccess != null

    LaunchedEffect(Unit) {
        viewModel.loadApps()
        viewModel.loadAllTypes()
        viewModel.loadAllTags()
    }

    LaunchedEffect(crudState) {
        if (crudState is AppsCRUDState.Success) {
            viewModel.loadApps()
            viewModel.resetState()
        }
    }

    Box(
        modifier = Modifier.fillMaxWidth().fillMaxHeight().backgroundColor(WhiteBG),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = listState) {
            is AppsListState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    AppsCRUDListContent(
                        state = cached,
                        selectedTypes = selectedTypes,
                        allTypes = allTypes,
                        allTags = allTags,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMoreInCurrentView = hasMoreInCurrentView,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                        onAddClick = { navigator.goToAppsAddPage() },
                        onAppClick = { navigator.goToAppsUpdatePage(it) },
                        onDeleteClick = { viewModel.deleteApp(it) },
                    )
                } else {
                    LoadingIndicator()
                }
            }

            is AppsListState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.message,
                onTryAgain = { viewModel.loadApps() },
            )

            is AppsListState.Success -> AppsCRUDListContent(
                state = state,
                selectedTypes = selectedTypes,
                allTypes = allTypes,
                allTags = allTags,
                searchText = searchText,
                isLoadingMore = isLoadingMore,
                hasMoreInCurrentView = hasMoreInCurrentView,
                onTypeToggle = { viewModel.toggleType(it) },
                onSearchChange = { viewModel.setSearchText(it) },
                onClearFilters = { viewModel.clearAllFilters() },
                onLoadMore = { viewModel.loadMoreIfNeeded() },
                onAddClick = { navigator.goToAppsAddPage() },
                onAppClick = { navigator.goToAppsUpdatePage(it) },
                onDeleteClick = { viewModel.deleteApp(it) },
            )
        }

        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun AppsCRUDListContent(
    state: AppsListState.Success,
    selectedTypes: Set<String>,
    allTypes: List<String>,
    allTags: List<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMoreInCurrentView: Boolean,
    onTypeToggle: (String) -> Unit,
    onSearchChange: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
    onAddClick: () -> Unit,
    onAppClick: (AppsResponse) -> Unit,
    onDeleteClick: (Long) -> Unit,
) {
    DisposableEffect(hasMoreInCurrentView) {
        val handler: (Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMoreInCurrentView && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    val hasActiveFilters = selectedTypes.isNotEmpty() || searchText.isNotBlank()

    Column(
        modifier = if (isMobile)
            Modifier.fillMaxWidth().padding(leftRight = 16.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem),
    ) {
        // Header
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 80.px, bottom = 32.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                SpanText(
                    text = "Manage Games & Apps",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(32.px)
                        .fontWeight(FontWeight.Bold)
                        .moxTextGradient(),
                )
                SpanText(
                    text = "${state.apps.size} apps loaded",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray),
                )
            }
            GradientButton(text = "+ Add App", onClick = onAddClick)
        }

        // Search bar + Clear Filters
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.px),
        ) {
            Box(modifier = Modifier.flexGrow(1)) {
                SearchBarWithAutocomplete(
                    value = searchText,
                    onValueChange = onSearchChange,
                    suggestions = allTags,
                    placeholder = "Search games & apps…",
                    onSearch = onSearchChange,
                )
            }

            if (hasActiveFilters) {
                Row(
                    modifier = PressableButtonStyle.toModifier()
                        .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                        .borderRadius(16.px)
                        .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                        .styleModifier { property("background", "#FFFFFF") }
                        .onClick { onClearFilters() },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "Clear Filters",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                            .moxTextGradient(),
                    )
                }
            }
        }

        // Type chips
        if (allTypes.isNotEmpty()) {
            Row(
                modifier = ChipRowScrollStyle.toModifier()
                    .fillMaxWidth()
                    .padding(top = 16.px, bottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                allTypes.forEach { t ->
                    Chip(
                        description = t,
                        isSelected = t in selectedTypes,
                        onChipClick = { onTypeToggle(t) },
                    )
                }
            }
        }

        // Apps grid
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = 30.px)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
            horizontalArrangement = Arrangement.spacedBy(24.px),
        ) {
            state.apps.forEach { app ->
                ContentCard(
                    imageSrc = app.imagePath.orEmpty(),
                    type = app.type,
                    title = app.title.orEmpty(),
                    description = app.description,
                    modifier = if (isMobile)
                        Modifier.fillMaxWidth().margin(bottom = 24.px)
                    else
                        Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 24.px),
                    onDeleteClick = {
                        if (window.confirm("Delete \"${app.title.orEmpty()}\"? This will also remove its images and cannot be undone.")) {
                            onDeleteClick(app.id)
                        }
                    },
                    actions = {
                        GradientButton(
                            text = "Edit",
                            buttonHeight = 42,
                            onClick = { onAppClick(app) },
                        )
                    },
                )
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) { LoadingMoreIndicator() }
        }
    }
}

